/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * useFxConversion Hook - React Hook for Currency Conversion
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Part of FinList FX Engine Phase 3
 * 
 * Provides an easy-to-use React hook for converting amounts between currencies.
 * Automatically fetches rates, handles loading states, and caches results.
 * 
 * Usage:
 *   const { convert, rate, loading, error } = useFxConversion('USD', 'NGN');
 *   const converted = convert(100); // Returns 165045.00
 * 
 *   // Or with formatting:
 *   const { convertFormatted } = useFxConversion('USD', 'NGN');
 *   const display = convertFormatted(100); // Returns "₦165,045.00"
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  getFxRate, 
  formatCurrency, 
  clearFxCache as clearCache 
} from '../services/fxClient';

/**
 * React hook for currency conversion
 * 
 * @param {string} fromCurrency - Source currency code (e.g., 'USD')
 * @param {string} toCurrency - Target currency code (e.g., 'NGN')
 * @param {object} options - Hook options
 * @param {boolean} options.autoFetch - Whether to fetch rate immediately (default: true)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 0 = disabled)
 * 
 * @returns {object} Conversion utilities and state
 */
export function useFxConversion(fromCurrency, toCurrency, options = {}) {
  const {
    autoFetch = true,
    refreshInterval = 0
  } = options;

  // State
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);  // When the FX API last updated the rate

  // Track mounted state to prevent updates after unmount
  const isMounted = useRef(true);

  // Normalize currency codes
  const from = useMemo(() => fromCurrency?.toUpperCase(), [fromCurrency]);
  const to = useMemo(() => toCurrency?.toUpperCase(), [toCurrency]);

  /**
   * Fetch the exchange rate
   */
  const fetchRate = useCallback(async () => {
    if (!from || !to) {
      setError('Both currencies must be specified');
      return null;
    }

    // Same currency = rate of 1
    if (from === to) {
      setRate(1);
      setLastUpdated(new Date().toISOString());
      setError(null);
      return 1;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getFxRate(from, to);
      
      if (isMounted.current) {
        setRate(result.rate);
        // Use the API's lastUpdated timestamp (when rates were actually updated)
        setLastUpdated(result.lastUpdated || new Date().toISOString());
        setError(null);
      }
      
      return result.rate;
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || 'Failed to fetch exchange rate');
      }
      return null;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [from, to]);

  /**
   * Convert an amount using the cached rate
   * Returns null if rate not loaded yet
   */
  const convert = useCallback((amount) => {
    if (rate === null) return null;
    
    const parsed = parseFloat(amount);
    if (isNaN(parsed)) return null;
    
    // Round to 2 decimal places
    return Math.round(parsed * rate * 100) / 100;
  }, [rate]);

  /**
   * Convert an amount and format with currency symbol
   */
  const convertFormatted = useCallback((amount) => {
    const converted = convert(amount);
    if (converted === null) return null;
    return formatCurrency(converted, to);
  }, [convert, to]);

  /**
   * Convert with async rate fetch (useful for one-off conversions)
   */
  const convertAsync = useCallback(async (amount) => {
    const parsed = parseFloat(amount);
    if (isNaN(parsed)) return null;

    try {
      const result = await getFxRate(from, to);
      return Math.round(parsed * result.rate * 100) / 100;
    } catch {
      return null;
    }
  }, [from, to]);

  /**
   * Force refresh the rate
   */
  const refresh = useCallback(() => {
    return fetchRate();
  }, [fetchRate]);

  /**
   * Clear all cached rates
   */
  const clearFxCache = useCallback(() => {
    clearCache();
    setRate(null);
    setLastUpdated(null);
  }, []);

  // Auto-fetch rate on mount or when currencies change
  useEffect(() => {
    if (autoFetch && from && to) {
      fetchRate();
    }
  }, [autoFetch, from, to, fetchRate]);

  // Set up auto-refresh interval if specified
  useEffect(() => {
    if (refreshInterval > 0 && from && to) {
      const intervalId = setInterval(fetchRate, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [refreshInterval, from, to, fetchRate]);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    // State
    rate,
    loading,
    error,
    lastUpdated,
    isReady: rate !== null && !loading,
    
    // Conversion functions
    convert,
    convertFormatted,
    convertAsync,
    
    // Actions
    refresh,
    clearFxCache,
    
    // Currency info
    from,
    to
  };
}

/**
 * Hook for managing user's comparison currency preference
 * Integrates with user settings in Firestore
 * 
 * @param {string} baseCurrency - User's base/primary currency
 * @returns {object} Comparison currency state and setter
 */
export function useComparisonCurrency(baseCurrency = 'USD') {
  const [comparisonCurrency, setComparisonCurrency] = useState(() => {
    // Try to get from localStorage first (quick sync)
    try {
      return localStorage.getItem('finlist_comparison_currency') || baseCurrency;
    } catch {
      return baseCurrency;
    }
  });

  // Persist to localStorage when changed
  const updateComparisonCurrency = useCallback((currency) => {
    const code = currency?.toUpperCase();
    if (!code) return;
    
    setComparisonCurrency(code);
    
    try {
      localStorage.setItem('finlist_comparison_currency', code);
    } catch {
      // Ignore localStorage errors
    }
    
    // Clear FX cache when comparison currency changes
    clearCache();
  }, []);

  return {
    comparisonCurrency,
    setComparisonCurrency: updateComparisonCurrency,
    isBaseCurrency: comparisonCurrency === baseCurrency?.toUpperCase()
  };
}

/**
 * Convenience hook that combines conversion with comparison currency
 * 
 * @param {string} baseCurrency - User's base currency (from settings)
 * @returns {object} Combined conversion utilities
 * 
 * @example
 * const { 
 *   comparisonCurrency, 
 *   setComparisonCurrency,
 *   convert, 
 *   convertFormatted 
 * } = useFxWithComparison('USD');
 * 
 * // User can change comparison currency
 * setComparisonCurrency('NGN');
 * 
 * // Then convert amounts
 * const inNaira = convert(100); // 165045.00
 */
export function useFxWithComparison(baseCurrency = 'USD') {
  const { comparisonCurrency, setComparisonCurrency, isBaseCurrency } = 
    useComparisonCurrency(baseCurrency);
  
  const fx = useFxConversion(baseCurrency, comparisonCurrency);

  return {
    ...fx,
    baseCurrency: baseCurrency?.toUpperCase(),
    comparisonCurrency,
    setComparisonCurrency,
    isBaseCurrency,
    
    // Override conversion to handle same currency case gracefully
    convert: useCallback((amount) => {
      if (isBaseCurrency) {
        const parsed = parseFloat(amount);
        return isNaN(parsed) ? null : parsed;
      }
      return fx.convert(amount);
    }, [isBaseCurrency, fx.convert]),
    
    convertFormatted: useCallback((amount) => {
      if (isBaseCurrency) {
        const parsed = parseFloat(amount);
        return isNaN(parsed) ? null : formatCurrency(parsed, baseCurrency);
      }
      return fx.convertFormatted(amount);
    }, [isBaseCurrency, baseCurrency, fx.convertFormatted])
  };
}

export default useFxConversion;
